import { injectCSS, styleObjectToCSS } from '../utils/css-in-js';
import {
  ListingRecommendation,
  RecommendationCache,
  WidgetDependencies,
  ProfileData,
  LevelData,
  BrandingData,
  ChannelData,
  ContentCache,
} from './types';
import { getCommunicationManager } from './iframe-communication';
import { STOPWORDS } from './stopwords';
import { WIDGET_STYLES } from './widget-styles';
import { CACHE_EXPIRATION_MS, CACHE_KEYS, CONTENT_CACHE_EXPIRY, URL_TRACKING_CONFIG } from './constants';

export class SimpleWidget {
  private container: HTMLDivElement | null = null;
  private dependencies: WidgetDependencies;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private updateDebounceTimer: NodeJS.Timeout | null = null;
  private containerClickListener: ((event: MouseEvent) => void) | null = null;

  private isAuthenticated = false;
  private profileData: ProfileData | null = null;
  private levelData: LevelData | null = null;

  private forceLoading = false;

  private brandingData: BrandingData | null = null;
  private channelData: ChannelData | null = null;

  private hasReceivedIframeAuthState = false;
  private lastRenderedState = '';
  private hasInitialAuthStructure = false;

  private currentUrl = '';
  private currentRecommendation: ListingRecommendation | null = null;
  private persistentRecommendations = new Map<string, ListingRecommendation>();
  private urlChangeListener: (() => void) | null = null;
  private urlDebounceTimer: NodeJS.Timeout | null = null;
  private recommendationCache = new Map<string, RecommendationCache>();
  private contentCache: ContentCache | null = null;
  private lastRecommendationFetch = 0;
  private lastRecommendationFetchByUrl = new Map<string, number>();

  private executionGuards = new Set<string>();
  private lastDataChange = new Map<string, number>();

  private lastAvatarState = '';

  private imageLoadingStates = new Map<string, 'loading' | 'loaded' | 'error'>();
  private imageCache = new Map<string, { timestamp: number; url: string }>();

  constructor(dependencies: WidgetDependencies) {
    this.dependencies = dependencies;
  }

  /**
   * Optimize image URL with resize and compression parameters
   */
  private optimizeImageUrl(
    originalUrl: string,
    options: { width?: number; height?: number; quality?: number } = {},
  ): string {
    if (!originalUrl) return '';

    try {
      const url = new URL(originalUrl);

      if (
        url.hostname.includes('cloudinary') ||
        url.hostname.includes('imgix') ||
        url.hostname.includes('cloudfront')
      ) {
        if (options.width) url.searchParams.set('w', options.width.toString());
        if (options.height) url.searchParams.set('h', options.height.toString());
        if (options.quality) url.searchParams.set('q', options.quality.toString());
        url.searchParams.set('f', 'auto');
        url.searchParams.set('c', 'fill');
      }

      return url.toString();
    } catch (error) {
      return originalUrl;
    }
  }

  /**
   * Preload image and track loading state
   */
  private async preloadImage(url: string, timeout = 5000): Promise<boolean> {
    if (!url || this.imageLoadingStates.get(url) === 'loaded') {
      return true;
    }

    this.imageLoadingStates.set(url, 'loading');

    return new Promise((resolve) => {
      const img = new Image();
      const timeoutId = setTimeout(() => {
        this.imageLoadingStates.set(url, 'error');
        resolve(false);
      }, timeout);

      img.onload = () => {
        clearTimeout(timeoutId);
        this.imageLoadingStates.set(url, 'loaded');
        this.debouncedUpdateContent();
        resolve(true);
      };

      img.onerror = () => {
        clearTimeout(timeoutId);
        this.imageLoadingStates.set(url, 'error');
        this.debouncedUpdateContent();
        resolve(false);
      };

      img.src = url;
    });
  }

  /**
   * Get cached optimized image URL or create new one
   */
  private getCachedImageUrl(
    originalUrl: string,
    options: { width?: number; height?: number; quality?: number } = {},
  ): string {
    if (!originalUrl) return '';

    const cacheKey = `${originalUrl}_${JSON.stringify(options)}`;
    const cached = this.imageCache.get(cacheKey);
    const now = Date.now();

    if (cached && now - cached.timestamp < 24 * 60 * 60 * 1000) {
      return cached.url;
    }

    const optimizedUrl = this.optimizeImageUrl(originalUrl, options);
    this.imageCache.set(cacheKey, { timestamp: now, url: optimizedUrl });

    if (this.imageCache.size > 50) {
      const oldestKey = this.imageCache.keys().next().value;
      if (oldestKey) {
        this.imageCache.delete(oldestKey);
      }
    }

    return optimizedUrl;
  }

  /**
   * Check if image is currently loading
   */
  private isImageLoading(url: string): boolean {
    return this.imageLoadingStates.get(url) === 'loading';
  }

  /**
   * Check if image has loaded successfully
   */
  private isImageLoaded(url: string): boolean {
    return this.imageLoadingStates.get(url) === 'loaded';
  }

  /**
   * Check if image failed to load
   */
  private hasImageFailed(url: string): boolean {
    return this.imageLoadingStates.get(url) === 'error';
  }

  /**
   * Save data to localStorage with timestamp
   */
  private saveToCache<T>(key: string, data: T): void {
    try {
      const cacheEntry = {
        data,
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(cacheEntry));
    } catch (error) {
      console.warn(`⚠️ [SimpleWidget] Failed to cache data for key: ${key}`, error);
    }
  }

  /**
   * Load all cached data immediately on mount
   */
  private loadAllCachedData(): void {
    try {
      const cachedAuthState = localStorage.getItem(CACHE_KEYS.AUTH_STATE);
      if (cachedAuthState) {
        const cacheEntry = JSON.parse(cachedAuthState);
        if (Date.now() - cacheEntry.timestamp <= CACHE_EXPIRATION_MS) {
          this.isAuthenticated = cacheEntry.data;
        }
      }

      const cachedIframeFlag = localStorage.getItem(CACHE_KEYS.IFRAME_AUTH_FLAG);
      if (cachedIframeFlag) {
        const cacheEntry = JSON.parse(cachedIframeFlag);
        if (Date.now() - cacheEntry.timestamp <= CACHE_EXPIRATION_MS) {
          this.hasReceivedIframeAuthState = cacheEntry.data;
        }
      }

      const cachedProfile = localStorage.getItem(CACHE_KEYS.PROFILE_DATA);
      if (cachedProfile) {
        const cacheEntry = JSON.parse(cachedProfile);
        if (Date.now() - cacheEntry.timestamp <= CACHE_EXPIRATION_MS) {
          this.profileData = cacheEntry.data;
        }
      }

      const cachedLevel = localStorage.getItem(CACHE_KEYS.LEVEL_DATA);
      if (cachedLevel) {
        const cacheEntry = JSON.parse(cachedLevel);
        if (Date.now() - cacheEntry.timestamp <= CACHE_EXPIRATION_MS) {
          this.levelData = cacheEntry.data;
        }
      }

      const cachedBranding = localStorage.getItem(CACHE_KEYS.BRANDING_DATA);
      if (cachedBranding) {
        const cacheEntry = JSON.parse(cachedBranding);
        if (Date.now() - cacheEntry.timestamp <= CACHE_EXPIRATION_MS) {
          this.brandingData = cacheEntry.data;
        }
      }

      const cachedChannel = localStorage.getItem(CACHE_KEYS.CHANNEL_DATA);
      if (cachedChannel) {
        const cacheEntry = JSON.parse(cachedChannel);
        if (Date.now() - cacheEntry.timestamp <= CACHE_EXPIRATION_MS) {
          this.channelData = cacheEntry.data;
        }
      }
    } catch (error) {
      console.warn('⚠️ [SimpleWidget] Failed to load cached data:', error);
    }
  }

  /**
   * Cache auth state changes
   */
  private cacheAuthState(): void {
    this.saveToCache(CACHE_KEYS.AUTH_STATE, this.isAuthenticated);
    this.saveToCache(CACHE_KEYS.IFRAME_AUTH_FLAG, this.hasReceivedIframeAuthState);
  }

  /**
   * STRONG RECOMMENDATION SYSTEM - Guaranteed to show when received
   * This method ensures recommendations are ALWAYS displayed, regardless of other state changes
   */
  private setRecommendationPersistently(url: string, recommendation: ListingRecommendation | null): void {
    if (recommendation) {
      this.persistentRecommendations.set(url, recommendation);
      this.currentRecommendation = recommendation;

      this.lastRenderedState = '';
      this.debouncedUpdateContent();
    } else {
      this.persistentRecommendations.delete(url);
      this.currentRecommendation = null;

      this.lastRenderedState = '';
      this.debouncedUpdateContent();
    }
  }

  /**
   * Get current recommendation for URL with persistence check
   */
  private getCurrentRecommendation(): ListingRecommendation | null {
    const persistentRec = this.persistentRecommendations.get(this.currentUrl);
    if (persistentRec) {
      this.currentRecommendation = persistentRec;
      return persistentRec;
    }
    return this.currentRecommendation;
  }

  /**
   * Prevent duplicate executions within 100ms for same operation + data
   */
  private shouldExecute(operationKey: string, data?: any): boolean {
    const now = Date.now();
    const dataKey = `${operationKey}_${JSON.stringify(data || {})}`;

    const lastExecution = this.lastDataChange.get(dataKey);
    if (lastExecution && now - lastExecution < 100) {
      return false;
    }

    this.lastDataChange.set(dataKey, now);

    if (this.lastDataChange.size > 20) {
      const oldestKey = this.lastDataChange.keys().next().value;
      if (oldestKey) {
        this.lastDataChange.delete(oldestKey);
      }
    }

    return true;
  }

  /**
   * Setup URL tracking system for recommendations
   */
  private setupUrlTracking(): void {
    this.currentUrl = this.normalizeUrl(window.location.href);

    this.urlChangeListener = this.debounce(() => {
      this.handleUrlChange();
    }, URL_TRACKING_CONFIG.debounceMs);

    window.addEventListener('popstate', this.urlChangeListener);
    this.wrapHistoryMethods();

    if (this.isAuthenticated) {
      if (this.profileData?.profile?.id && this.channelData?.channel?.id) {
        this.requestSingleRecommendation('initial_setup');
      }
    }
  }

  /**
   * Wrap history methods to detect programmatic navigation
   */
  private wrapHistoryMethods(): void {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = (...args) => {
      originalPushState.apply(window.history, args);
      if (this.urlChangeListener) {
        this.urlChangeListener();
      }
    };

    window.history.replaceState = (...args) => {
      originalReplaceState.apply(window.history, args);
      if (this.urlChangeListener) {
        this.urlChangeListener();
      }
    };
  }

  /**
   * Handle URL changes
   */
  private handleUrlChange(): void {
    const newUrl = this.normalizeUrl(window.location.href);

    if (newUrl === this.currentUrl) {
      return; // No actual change
    }

    this.currentUrl = newUrl;

    // Track the page change event if authenticated
    if (this.isAuthenticated) {
      // Single controlled recommendation fetch
      this.requestSingleRecommendation('url_change');
    }
  }

  /**
   * Extract keywords from current page content with intelligent caching
   */
  private extractKeywords(): string[] {
    try {
      // Create content hash for caching
      const title = document.title || '';
      const metaDescription = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
      const h1Elements = Array.from(document.querySelectorAll('h1'))
        .map((el) => el.textContent)
        .join(' ');
      const h2Elements = Array.from(document.querySelectorAll('h2'))
        .map((el) => el.textContent)
        .join(' ');

      // Create hash from key content elements (more efficient than full body text)
      const keyContent = `${title} ${metaDescription} ${h1Elements} ${h2Elements}`.toLowerCase();
      const contentHash = this.simpleHash(keyContent);

      // Check cache first
      if (
        this.contentCache &&
        this.contentCache.hash === contentHash &&
        Date.now() - this.contentCache.timestamp < CONTENT_CACHE_EXPIRY
      ) {
        return this.contentCache.keywords;
      }

      // Extract keywords from key content areas only (more targeted and faster)
      const mainContent =
        document.querySelector('main')?.textContent ||
        document.querySelector('article')?.textContent ||
        document.querySelector('.content')?.textContent ||
        document.body.textContent ||
        '';

      // Focus on title, headings, and first paragraph for better relevance
      const focusedContent = `${title} ${h1Elements} ${h2Elements} ${mainContent.substring(0, 1000)}`.toLowerCase();

      // Optimized word extraction
      const words = focusedContent
        .replace(/[^\w\s]/g, ' ')
        .split(/\s+/)
        .filter((word) => word.length > 2 && word.length < 15 && !STOPWORDS.has(word) && !/^\d+$/.test(word));

      // Simple frequency count (faster than Map for small datasets)
      const wordCount: { [key: string]: number } = {};
      words.forEach((word) => {
        wordCount[word] = (wordCount[word] || 0) + 1;
      });

      // Get top keywords
      const keywords = Object.entries(wordCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, URL_TRACKING_CONFIG.maxKeywords)
        .map(([word]) => word);

      // Cache the result
      this.contentCache = {
        hash: contentHash,
        keywords,
        timestamp: Date.now(),
      };

      return keywords;
    } catch (error) {
      console.warn('⚠️ [SimpleWidget] Error extracting keywords:', error);
      return [];
    }
  }

  /**
   * Simple hash function for content caching
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash * 31 + char) % 2147483647; // Use multiplication instead of bitwise operations
      hash = Math.abs(hash); // Ensure positive
    }
    return hash.toString(36);
  }

  /**
   * Normalize URL for consistent comparison and caching
   */
  private normalizeUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      urlObj.hash = ''; // Ignore hash fragments

      // Sort query params for consistent comparison
      const params = Array.from(urlObj.searchParams.entries()).sort((a, b) => a[0].localeCompare(b[0]));
      urlObj.search = '';
      params.forEach(([key, value]) => urlObj.searchParams.append(key, value));

      // Normalize trailing slash
      let pathname = urlObj.pathname;
      if (pathname.endsWith('/') && pathname.length > 1) {
        pathname = pathname.slice(0, -1);
      }
      urlObj.pathname = pathname;

      return urlObj.toString();
    } catch {
      return url;
    }
  }

  /**
   * STRONG RECOMMENDATION SYSTEM - Single controlled request method
   * This prevents redundant calls and ensures recommendations are always shown when received
   */
  private requestSingleRecommendation(reason: string): void {
    // Must be authenticated and have required data
    if (!this.isAuthenticated || !this.profileData?.profile?.id || !this.channelData?.channel?.id) {
      return;
    }

    // Enhanced rate limiting - prevent rapid successive requests per URL
    const now = Date.now();
    const rateLimitKey = `${this.currentUrl}_${reason}`;

    // Stricter rate limiting: minimum 3 seconds between requests for same URL
    const lastFetch = this.lastRecommendationFetchByUrl.get(this.currentUrl) || 0;
    if (now - lastFetch < 3000) {
      return;
    }

    // Check if we already have a recommendation for current URL
    if (this.persistentRecommendations.has(this.currentUrl)) {
      return;
    }

    // Prevent duplicate execution protection
    if (!this.shouldExecute(`requestRecommendation_${rateLimitKey}`)) {
      return;
    }

    this.lastRecommendationFetch = now;
    this.lastRecommendationFetchByUrl.set(this.currentUrl, now);
    this.fetchRecommendationForCurrentPage(reason);
  }

  /**
   * Fetch recommendation with strong persistence guarantee
   */
  private async fetchRecommendationForCurrentPage(_reason: string): Promise<void> {
    if (!this.isAuthenticated || !this.profileData?.profile?.id || !this.channelData?.channel?.id) {
      return;
    }

    const keywords = this.extractKeywords();
    if (keywords.length === 0) {
      this.setRecommendationPersistently(this.currentUrl, null);
      return;
    }

    const cacheKey = `${this.currentUrl}_${keywords.sort().join('_')}`;

    // Check cache first
    const cached = this.recommendationCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_EXPIRATION_MS) {
      this.setRecommendationPersistently(this.currentUrl, cached.recommendation);
      return;
    }

    try {
      const iframe = this.dependencies.sdk?.overlay?.iframe;
      if (!iframe) {
        this.setRecommendationPersistently(this.currentUrl, null);
        return;
      }

      const communicationManager = getCommunicationManager(iframe);

      const recommendation = await communicationManager.requestRecommendation(this.currentUrl, keywords, {
        channelId: this.channelData.channel.id,
        profileId: this.profileData.profile.id,
        timestamp: Date.now(),
      });

      // Cache the result
      this.recommendationCache.set(cacheKey, {
        recommendation,
        timestamp: Date.now(),
        url: this.currentUrl,
        keywords,
      });

      // Clean old cache entries based on timestamp
      if (this.recommendationCache.size > 50) {
        let oldestKey: string | null = null;
        let oldestTimestamp = Infinity;

        const entries = Array.from(this.recommendationCache.entries());
        for (const [key, value] of entries) {
          if (value.timestamp < oldestTimestamp) {
            oldestTimestamp = value.timestamp;
            oldestKey = key;
          }
        }

        if (oldestKey !== null) {
          this.recommendationCache.delete(oldestKey);
        }
      }

      // GUARANTEE: Always set recommendation persistently
      this.setRecommendationPersistently(this.currentUrl, recommendation);
    } catch (error) {
      // Cache failed request to avoid immediate retry
      this.recommendationCache.set(cacheKey, {
        recommendation: null,
        timestamp: Date.now(),
        url: this.currentUrl,
        keywords,
      });

      this.setRecommendationPersistently(this.currentUrl, null);
    }
  }

  /**
   * Cleanup URL tracking
   */
  private cleanupUrlTracking(): void {
    if (this.urlChangeListener) {
      window.removeEventListener('popstate', this.urlChangeListener);
      this.urlChangeListener = null;
    }

    if (this.urlDebounceTimer) {
      clearTimeout(this.urlDebounceTimer);
      this.urlDebounceTimer = null;
    }
  }

  /**
   * Debounce utility function
   */
  private debounce(func: Function, wait: number): () => void {
    return () => {
      if (this.urlDebounceTimer) {
        clearTimeout(this.urlDebounceTimer);
      }
      this.urlDebounceTimer = setTimeout(() => func.apply(this), wait);
    };
  }

  /**
   * Public getter for container access
   */
  public getContainer(): HTMLDivElement | null {
    return this.container;
  }

  /**
   * Get the current pixel position with priority logic
   */
  public getCurrentPosition(): string {
    return this.getPixelPosition();
  }

  public mount(): void {
    this.injectStyles();
    this.loadAllCachedData();

    this.createWidget();
    this.setupPostMessageListener();
    this.setupInitialAuthCheck();
    this.setupResizeListener();
    this.setupUrlTracking();
    this.requestIframeDataIfReady();
    this.debouncedUpdateContent();
  }

  /**
   * Setup resize listener to handle mobile ↔ desktop transitions
   */
  private setupResizeListener(): void {
    let resizeTimer: NodeJS.Timeout | null = null;

    window.addEventListener('resize', () => {
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }

      resizeTimer = setTimeout(() => {
        this.updateMobileClass();
        this.debouncedUpdateContent();
        resizeTimer = null;
      }, 150);
    });
  }

  /**
   * Try to request fresh data from iframe immediately if it has finished loading
   * This leverages the preloaded iframe to get data faster
   */
  private requestIframeDataIfReady(): void {
    const iframe = this.dependencies.sdk?.overlay?.iframe;
    if (!iframe) return;

    if (iframe.contentDocument?.readyState === 'complete') {
      this.requestFreshDataFromIframe();
    } else {
      iframe.addEventListener(
        'load',
        () => {
          setTimeout(() => {
            this.requestFreshDataFromIframe();
          }, 100);
        },
        { once: true },
      );
    }
  }

  /**
   * Request fresh data from the preloaded iframe
   */
  private requestFreshDataFromIframe(): void {
    try {
      const iframe = this.dependencies.sdk?.overlay?.iframe;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: 'TRONIC_REQUEST_FRESH_DATA',
            timestamp: Date.now(),
          },
          '*',
        );
      }
    } catch (error) {
      console.warn('⚠️ [SimpleWidget] Failed to request fresh data from iframe:', error);
    }
  }

  private injectStyles(): void {
    injectCSS(styleObjectToCSS(WIDGET_STYLES), 'tronic-simple-widget-styles');
  }

  /**
   * Get pixel position with priority: channelData.metadata.pixel.pixel_position > config > default
   */
  private getPixelPosition(): string {
    const channelPosition = this.channelData?.channel?.metadata?.pixel?.pixel_position;
    if (channelPosition && this.isValidPosition(channelPosition)) {
      this.dependencies.config.position = channelPosition;
      return channelPosition;
    }

    const configPosition = this.dependencies.config.position;
    if (configPosition && this.isValidPosition(configPosition)) {
      return configPosition;
    }

    return 'bottom-right';
  }

  /**
   * Validate if a position value is valid
   */
  private isValidPosition(position: any): boolean {
    const validPositions = ['top-right', 'bottom-right', 'bottom-left'];
    return typeof position === 'string' && validPositions.includes(position);
  }

  /**
   * Notify SDK that position might have changed
   */
  private notifyPositionChange(): void {
    const event = new CustomEvent('tronic-launcher-position-changed', {
      detail: { position: this.getPixelPosition() },
    });
    window.dispatchEvent(event);
  }

  private createWidget(): void {
    if (this.container) {
      this.updateWidgetPosition();
      this.updateMobileClass();
      return;
    }

    this.destroy();

    this.container = document.createElement('div');
    this.container.className = 'tronic-simple-widget';

    const position = this.getPixelPosition();
    this.container.classList.add(`position-${position}`);

    const theme = this.dependencies.config.theme || 'light';
    this.container.classList.add(`theme-${theme}`);

    if (!this.isAuthenticated) {
      this.container.classList.add('unauthenticated');
    }

    this.updateMobileClass();

    document.body.appendChild(this.container);
  }

  /**
   * Update mobile class based on current window width
   */
  private updateMobileClass(): void {
    if (!this.container) return;

    const isMobile = window.innerWidth <= 600;

    if (isMobile) {
      this.container.classList.add('mobile');
    } else {
      this.container.classList.remove('mobile');
    }
  }

  /**
   * Update widget position without destroying state
   */
  private updateWidgetPosition(): void {
    if (!this.container) return;

    this.container.classList.remove('position-top-right', 'position-bottom-right', 'position-bottom-left');

    const position = this.getPixelPosition();
    this.container.classList.add(`position-${position}`);

    this.container.classList.remove('theme-light', 'theme-dark');
    const theme = this.dependencies.config.theme || 'light';
    this.container.classList.add(`theme-${theme}`);
  }

  private setupPostMessageListener(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }

    this.messageListener = (event: MessageEvent) => {
      if (
        !event.origin.includes('tronic.app') &&
        !event.origin.includes('localhost') &&
        !event.origin.includes('127.0.0.1')
      ) {
        return;
      }

      const { type, payload } = event.data || {};

      switch (type) {
        case 'TRONIC_SESSION_CHANGED':
          this.handleSessionChanged(payload);
          break;

        case 'TRONIC_PROFILE_DATA_CHANGED':
          this.handleDataChanged('profile', payload);
          break;

        case 'TRONIC_LEVEL_DATA_CHANGED':
        case 'TRONIC_POINTS_EARNED':
        case 'TRONIC_LEVEL_UP':
          this.handleDataChanged('level', payload);
          break;

        case 'TRONIC_BRANDING_CHANGED':
          this.handleDataChanged('branding', payload);
          break;

        case 'TRONIC_CHANNEL_DATA_CHANGED':
          this.handleDataChanged('channel', payload);
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  private setupInitialAuthCheck(): void {
    const checkAuthState = () => {
      if (this.hasReceivedIframeAuthState) {
        return;
      }

      const sessionToken = localStorage.getItem('stytch_session_jwt');
      const wasAuthenticated = this.isAuthenticated;
      this.isAuthenticated = !!sessionToken;

      if (wasAuthenticated !== this.isAuthenticated) {
        this.debouncedUpdateContent();
      }
    };

    checkAuthState();

    window.addEventListener('storage', (event) => {
      if (event.key === 'stytch_session_jwt') {
        checkAuthState();
      }
    });

    window.addEventListener('focus', checkAuthState);
  }

  private debouncedUpdateContent(): void {
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }

    this.updateDebounceTimer = setTimeout(() => {
      this.updateContent();
      this.updateDebounceTimer = null;
    }, 150);
  }

  private forceImmediateUpdate(): void {
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
      this.updateDebounceTimer = null;
    }

    this.lastRenderedState = '';
    this.updateContent();
  }

  private updateContent(): void {
    if (!this.container) return;

    this.getCurrentRecommendation();

    const currentState = this.generateStateHash();

    if (currentState === this.lastRenderedState) {
      return;
    }

    this.container.style.transition = 'opacity 0.2s ease-in-out';

    if (this.isAuthenticated) {
      this.container.classList.remove('unauthenticated');
      this.updateAuthenticatedContent();
    } else {
      this.container.classList.add('unauthenticated');
      this.hasInitialAuthStructure = false; // Reset flag when switching to unauthenticated
      this.updateUnauthenticatedContent();
    }

    this.lastRenderedState = currentState;
    this.attachEventHandlers();
  }

  // Generate a hash of current state for comparison
  private generateStateHash(): string {
    // Get image URLs for state tracking
    const channelLogo = this.channelData?.channelLogo || this.brandingData?.channelLogo;
    const userAvatar = this.profileData?.profilePic;
    const recommendationImage = this.currentRecommendation?.metadata?.bannerImage;

    // Optimize URLs for consistent state tracking
    const optimizedChannelLogo = channelLogo
      ? this.getCachedImageUrl(channelLogo, { width: 56, height: 56, quality: 80 })
      : '';
    const optimizedUserAvatar = userAvatar
      ? this.getCachedImageUrl(userAvatar, { width: 64, height: 64, quality: 80 })
      : '';
    const optimizedRecommendationImage = recommendationImage
      ? this.getCachedImageUrl(recommendationImage, { width: 200, height: 120, quality: 85 })
      : '';

    const stateObj = {
      isAuthenticated: this.isAuthenticated,
      userName: this.profileData?.profile?.firstName,
      userAvatar: this.profileData?.profilePic,
      points: this.levelData?.myPoints,
      pointsToNext: this.levelData?.pointsToNextLevel,
      currentLevel: this.levelData?.currentLevelName,
      progress: this.levelData?.progress,
      isMobile: window.innerWidth <= 600, // Include mobile state in hash
      channelLogo: this.channelData?.channelLogo || this.brandingData?.channelLogo,
      // Recommendation state
      hasRecommendation: !!this.currentRecommendation,
      recommendationId: this.currentRecommendation?.id,
      recommendationName: this.currentRecommendation?.name,
      recommendationImage: this.currentRecommendation?.metadata?.bannerImage,
      // Image loading states - this is crucial for UI updates
      channelLogoState: optimizedChannelLogo ? this.imageLoadingStates.get(optimizedChannelLogo) || 'pending' : 'none',
      userAvatarState: optimizedUserAvatar ? this.imageLoadingStates.get(optimizedUserAvatar) || 'pending' : 'none',
      recommendationImageState: optimizedRecommendationImage
        ? this.imageLoadingStates.get(optimizedRecommendationImage) || 'pending'
        : 'none',
    };

    return JSON.stringify(stateObj);
  }

  // Optimized update for authenticated content - selective updates to prevent flickers
  private updateAuthenticatedContent(): void {
    if (!this.container) return;

    const existingContent = this.container.querySelector('.tronic-content');
    const isMobile = window.innerWidth <= 600;

    // Check if we need full regeneration (first render, mobile/desktop switch, or major structure change)
    const needsFullRegeneration =
      !existingContent || !this.hasInitialAuthStructure || this.container.classList.contains('mobile') !== isMobile;

    if (needsFullRegeneration) {
      // Full regeneration needed
      this.container.innerHTML = this.generateAuthenticatedContent();
      this.hasInitialAuthStructure = true;
      return;
    }

    // Selective updates for better performance
    this.updateAuthenticatedContentSelectively();
  }

  private updateAuthenticatedContentSelectively(): void {
    if (!this.container) return;

    // Update avatar
    const avatarContainer = this.container.querySelector('.tronic-avatar');
    if (avatarContainer) {
      const userAvatar = this.profileData?.profilePic;
      const userName = this.profileData?.profile?.firstName || 'User';
      const optimizedAvatar = userAvatar
        ? this.getCachedImageUrl(userAvatar, { width: 64, height: 64, quality: 85 })
        : '';

      if (optimizedAvatar && this.isImageLoaded(optimizedAvatar)) {
        avatarContainer.innerHTML = `<img src="${optimizedAvatar}" alt="Profile" loading="lazy" />`;
      } else if (!optimizedAvatar && this.profileData?.profile) {
        avatarContainer.innerHTML = `<div class="tronic-default-avatar">${userName.charAt(0).toUpperCase()}</div>`;
      }
    }

    // Update username
    const nameElement = this.container.querySelector('.tronic-user-name');
    if (nameElement) {
      const userName = this.profileData?.profile?.firstName || 'User';
      if (nameElement.textContent !== userName) {
        nameElement.textContent = userName;
      }
    }

    // Update points and progress if visible (not replaced by recommendation)
    const currentRecommendation = this.getCurrentRecommendation();
    if (!currentRecommendation) {
      const pointsValue = this.container.querySelector('.tronic-points-value');
      const pointsNext = this.container.querySelector('.tronic-points-next');

      if (pointsValue && this.levelData) {
        const points = this.levelData.myPoints || 0;
        const newPointsText = `${points} Points`;
        if (pointsValue.textContent !== newPointsText) {
          pointsValue.textContent = newPointsText;
        }
      }

      if (pointsNext && this.levelData) {
        const pointsToNext = this.levelData.pointsToNextLevel || 0;
        const newNextText = `Next Lvl: ${this.levelData.isMaxLevel ? 'Max' : pointsToNext}`;
        if (pointsNext.textContent !== newNextText) {
          pointsNext.textContent = newNextText;
        }
      }

      // Update progress arc
      const progressPath = this.container.querySelector('.tronic-progress-arc path:last-child');
      if (progressPath && this.levelData) {
        const progress = this.levelData.progress || 0;
        const arcLength = Math.PI * 20;
        const progressDasharray = `${progress * arcLength} ${arcLength}`;
        progressPath.setAttribute('stroke-dasharray', progressDasharray);
      }

      // Update level text
      const levelText = this.container.querySelector('.tronic-progress-arc text');
      if (levelText && this.levelData) {
        const currentLevel = this.levelData.currentLevelName || '1';
        const newLevelText = `Lvl ${currentLevel}`;
        if (levelText.textContent !== newLevelText) {
          levelText.textContent = newLevelText;
        }
      }
    } else {
      // Recommendation is showing - need to check if content section needs update
      const contentSection = this.container.querySelector('.tronic-content-section');
      const hasRecommendation = contentSection?.querySelector('.tronic-recommendation-card');

      if (!hasRecommendation) {
        // Need to switch from points to recommendation - do full regeneration
        this.container.innerHTML = this.generateAuthenticatedContent();
        this.hasInitialAuthStructure = true;
      }
    }
  }

  // Selective update for unauthenticated content
  private updateUnauthenticatedContent(): void {
    const existingButton = this.container?.querySelector('.tronic-connect-button');

    if (!existingButton) {
      // First time render - need full HTML
      this.container!.innerHTML = this.generateUnauthenticatedContent();
    }
  }

  // Consolidated PostMessage Event Handlers

  private handleSessionChanged(payload: { isAuthenticated: boolean }): void {
    // Prevent duplicate session change processing
    if (!this.shouldExecute('handleSessionChanged', payload)) {
      return;
    }

    const wasAuthenticated = this.isAuthenticated;
    this.hasReceivedIframeAuthState = true;
    this.isAuthenticated = payload.isAuthenticated;
    this.cacheAuthState();

    if (!payload.isAuthenticated) {
      this.profileData = null;
      this.currentRecommendation = null;
      this.persistentRecommendations.clear(); // Clear persistent recommendations on logout
      this.recommendationCache.clear();
      this.cleanupUrlTracking();
      localStorage.removeItem(CACHE_KEYS.PROFILE_DATA);

      // Clear image loading states on logout
      this.imageLoadingStates.clear();
      this.imageCache.clear();

      // Reset structure flag on logout
      this.hasInitialAuthStructure = false;
    }

    if (wasAuthenticated !== this.isAuthenticated) {
      if (this.isAuthenticated && !wasAuthenticated) {
        this.setupUrlTracking();

        // Single controlled recommendation request if we have all required data
        if (this.profileData?.profile?.id && this.channelData?.channel?.id) {
          this.requestSingleRecommendation('auth_state_change');
        }
      }
      this.debouncedUpdateContent();
    }
  }

  private handleDataChanged(dataType: 'profile' | 'level' | 'branding' | 'channel', payload: any): void {
    // Prevent duplicate data change processing
    if (!this.shouldExecute(`handleDataChanged_${dataType}`, payload)) {
      return;
    }

    switch (dataType) {
      case 'profile':
        this.profileData = payload;
        this.saveToCache(CACHE_KEYS.PROFILE_DATA, payload);
        // Single controlled recommendation request if we have all required data
        if (this.isAuthenticated && this.profileData?.profile?.id && this.channelData?.channel?.id) {
          this.requestSingleRecommendation('profile_data_change');
        }
        break;
      case 'level':
        // Handle all level-related updates (points, level up, etc.)
        if (payload.newTotalPoints !== undefined) {
          // Points earned event
          this.levelData = {
            ...this.levelData,
            myPoints: payload.newTotalPoints,
            currentLevelName: payload.currentLevelName || this.levelData?.currentLevelName || '1',
            nextLevelName: payload.nextLevelName || this.levelData?.nextLevelName || '2',
            progress: payload.progress || 0,
            pointsToNextLevel: payload.pointsToNextLevel || 0,
            timestamp: payload.timestamp,
            isMaxLevel: payload.isMaxLevel ?? this.levelData?.isMaxLevel ?? false,
            currentLevel: payload.currentLevel || this.levelData?.currentLevel,
            nextLevel: payload.nextLevel || this.levelData?.nextLevel,
          };
        } else if (payload.totalPoints !== undefined) {
          // Level up event
          this.levelData = {
            ...this.levelData,
            myPoints: payload.totalPoints,
            currentLevelName: payload.newLevelName || payload.currentLevelName || '1',
            nextLevelName: payload.nextLevelName || this.levelData?.nextLevelName || '2',
            progress: payload.progress || 0,
            isMaxLevel: payload.isMaxLevel ?? false,
            pointsToNextLevel: payload.pointsToNextLevel || 0,
            timestamp: payload.timestamp,
            currentLevel: payload.currentLevel || this.levelData?.currentLevel,
            nextLevel: payload.nextLevel || this.levelData?.nextLevel,
          };
        } else {
          // Regular level data update
          this.levelData = payload;
        }
        this.saveToCache(CACHE_KEYS.LEVEL_DATA, this.levelData);
        break;
      case 'branding':
        this.brandingData = payload;
        this.saveToCache(CACHE_KEYS.BRANDING_DATA, payload);
        break;
      case 'channel':
        this.channelData = payload;
        this.saveToCache(CACHE_KEYS.CHANNEL_DATA, payload);
        // Update widget position if it changed
        this.updateWidgetPosition();
        // Notify SDK that position might have changed
        this.notifyPositionChange();
        // Single controlled recommendation request if we have all required data
        if (this.isAuthenticated && this.profileData?.profile?.id && this.channelData?.channel?.id) {
          this.requestSingleRecommendation('channel_data_change');
        }
        break;
      default:
        break;
    }

    this.debouncedUpdateContent();
  }

  // UI Generation Methods

  private generateAuthenticatedContent(): string {
    // 🚨 TEMPORAL: Force loading state for UI development
    const profileData = this.forceLoading ? null : this.profileData;
    const levelData = this.forceLoading ? null : this.levelData;

    // Check if we're in mobile mode
    const isMobile = window.innerWidth <= 600;

    // Get real data or fallbacks
    const userName = profileData?.profile?.firstName || 'User';
    const userAvatar = profileData?.profilePic;
    const points = levelData?.myPoints || 0;
    const pointsToNext = levelData?.pointsToNextLevel || 0;
    const currentLevel = levelData?.currentLevelName || '1';
    const progress = levelData?.progress || 0;

    // Calculate progress arc (if showing progress)
    const arcLength = Math.PI * 20; // radius = 20
    const progressDasharray = `${progress * arcLength} ${arcLength}`;

    // Avatar HTML with optimized loading
    let avatarHtml: string;
    const optimizedAvatar = userAvatar
      ? this.getCachedImageUrl(userAvatar, { width: 64, height: 64, quality: 85 })
      : '';

    if (optimizedAvatar) {
      const isLoaded = this.isImageLoaded(optimizedAvatar);
      const isLoading = this.isImageLoading(optimizedAvatar);
      const hasFailed = this.hasImageFailed(optimizedAvatar);

      if (isLoaded) {
        avatarHtml = `<img src="${optimizedAvatar}" alt="Profile" loading="lazy" />`;
      } else if (isLoading || (!isLoaded && !hasFailed)) {
        avatarHtml = `<div class="tronic-avatar-placeholder">
                        <div class="tronic-avatar-skeleton"></div>
                      </div>`;

        // Start preloading if not already started
        if (!isLoading && !isLoaded && !hasFailed) {
          this.preloadImage(optimizedAvatar, 7000).then((success) => {
            if (success || this.hasImageFailed(optimizedAvatar)) {
              this.debouncedUpdateContent();
            }
          });
        }
      } else if (hasFailed && profileData?.profile) {
        avatarHtml = `<div class="tronic-default-avatar">${userName.charAt(0).toUpperCase()}</div>`;
      } else {
        avatarHtml = `<div class="tronic-avatar-placeholder">
                        <div class="tronic-avatar-skeleton"></div>
                      </div>`;
      }
    } else if (profileData?.profile) {
      avatarHtml = `<div class="tronic-default-avatar">${userName.charAt(0).toUpperCase()}</div>`;
    } else {
      avatarHtml = `<div class="tronic-avatar-placeholder">
                      <div class="tronic-avatar-skeleton"></div>
                    </div>`;
    }

    // Get accent color from channel branding
    const accentColor =
      this.channelData?.channel?.theme?.colors?.accent?.bgColor || this.brandingData?.colors?.accent?.bgColor || 'gray'; // fallback

    // Recommendation or Points/Progress section
    let contentSectionHtml = '';

    // Always check persistent recommendations first
    const currentRecommendation = this.getCurrentRecommendation();

    if (currentRecommendation) {
      // Show recommendation instead of points/progress
      contentSectionHtml = this.generateRecommendationContent(currentRecommendation);
    } else {
      // Show original points/progress content
      // Progress arc HTML (show skeleton if no data, otherwise show arc if progress enabled)
      const progressArcHtml = levelData
        ? `
              <div class="tronic-progress-arc-container">
                <svg class="tronic-progress-arc" viewBox="0 0 50 30" width="54px" height="40px">
                  <path
                    d="M 5 25 A 20 20 0 0 1 45 25"
                    fill="transparent"
                    stroke="#e3e3e8"
                    stroke-width="4"
                    stroke-linecap="round"
                  />
                  <path
                    d="M 5 25 A 20 20 0 0 1 45 25"
                    fill="transparent"
                    stroke="${accentColor}"
                    stroke-width="5"
                    stroke-linecap="round"
                    stroke-dasharray="${progressDasharray}"
                    stroke-dashoffset="0"
                  />
                  <text
                    x="25"
                    y="24"
                    text-anchor="middle"
                    font-size="10"
                    fill="#e6e6ec"
                    style="user-select: none; font-weight: 500;"
                  >
                    Lvl ${currentLevel}
                  </text>
                </svg>
              </div>
            `
        : !levelData
        ? `
              <div class="tronic-progress-arc-container">
                <div class="tronic-progress-arc-skeleton"></div>
              </div>
            `
        : '';

      const pointsHtml = `
        <div class="tronic-points-info">
          ${
            levelData
              ? `<div class="tronic-points-value">${points} Points</div>
                 <div class="tronic-points-next">Next Lvl: ${levelData.isMaxLevel ? 'Max' : pointsToNext}</div>`
              : `<div class="tronic-points-placeholder">
                   <div class="tronic-points-skeleton"></div>
                   <div class="tronic-points-skeleton small"></div>
                 </div>`
          }
        </div>
      `;

      contentSectionHtml = `
        <div class="tronic-points-block">
          ${progressArcHtml}${pointsHtml}
        </div>
        <div class="tronic-separator"></div>
      `;
    }

    // Action buttons
    const actionButtonsHtml = `
      <div class="tronic-action-buttons">
        <button class="tronic-action-button" data-action="toggle-visibility" title="Open Wallet">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none" class="tronic-icon-open">
            <path d="M3.85714 9H1V6.14286M6.14286 1H9V3.85714" stroke="#E6E6EC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none" class="tronic-icon-close">
            <path d="M2 2L8 8M8 2L2 8" stroke="#E6E6EC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;

    return `
      <div class="tronic-authenticated-content">
        <div class="tronic-user-avatar">
          ${avatarHtml}
        </div>
        
        <div class="tronic-separator"></div>
        
        ${contentSectionHtml}
        
        ${actionButtonsHtml}
      </div>
    `;
  }

  /**
   * Generate HTML content for listing recommendation
   */
  private generateRecommendationContent(recommendation: ListingRecommendation): string {
    const isMobile = window.innerWidth <= 600;

    // Get accent color from channel branding
    const accentColor =
      this.channelData?.channel?.theme?.colors?.accent?.bgColor || this.brandingData?.colors?.accent?.bgColor || 'gray'; // fallback

    // Get recommendation image
    const recommendationImage = recommendation.metadata?.bannerImage;

    // Generate image HTML with notification indicator
    let imageHtml: string;
    if (recommendationImage) {
      imageHtml = `
        <img src="${recommendationImage}" alt="${recommendation.name}" />
        <div class="tronic-recommendation-indicator" style="background-color: ${accentColor};">!</div>
      `;
    } else {
      imageHtml = `
        <div class="tronic-recommendation-placeholder"></div>
        <div class="tronic-recommendation-indicator" style="background-color: ${accentColor};">!</div>
      `;
    }

    // Note: Subtitle removed as per UI requirements

    // In mobile, show more compact version
    if (isMobile) {
      return `
        <div class="tronic-recommendation-container" data-action="open-recommendation" data-listing-id="${recommendation.id}">
          <div class="tronic-recommendation-image">
            ${imageHtml}
          </div>
          <div class="tronic-recommendation-content">
            <div class="tronic-recommendation-title">${recommendation.name}</div>
          </div>
        </div>
        <div class="tronic-separator"></div>
      `;
    }

    // Desktop version with full content
    return `
      <div class="tronic-recommendation-container" data-action="open-recommendation" data-listing-id="${recommendation.id}">
        <div class="tronic-recommendation-image">
          ${imageHtml}
        </div>
        <div class="tronic-recommendation-content">
          <div class="tronic-recommendation-title">${recommendation.name}</div>
        </div>
      </div>
      <div class="tronic-separator"></div>
    `;
  }

  private generateUnauthenticatedContent(): string {
    return `<button class="tronic-connect-button" data-action="connect-wallet"><span class="connect-button-text">Play Now</span></button>`;
  }

  private cleanupEventHandlers(): void {
    if (!this.container || !this.containerClickListener) return;

    this.container.removeEventListener('click', this.containerClickListener);
    this.containerClickListener = null;
  }

  private attachEventHandlers(): void {
    if (!this.container) return;

    // Remove existing event listener to prevent duplicates
    this.cleanupEventHandlers();

    // Use event delegation on the container
    this.containerClickListener = async (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const actionElement = target.closest('[data-action]');

      if (!actionElement || !(actionElement instanceof HTMLElement)) return;

      const action = actionElement.getAttribute('data-action');

      switch (action) {
        case 'toggle-visibility':
          await this.handleToggleVisibility();
          break;
        case 'connect-wallet':
          await this.handleConnectWallet();
          break;
        case 'open-recommendation': {
          const listingId = actionElement.getAttribute('data-listing-id');
          if (listingId) {
            await this.handleRecommendationClick(listingId);
          }
          break;
        }
        default:
          // Unknown action, do nothing
          break;
      }
    };

    this.container.addEventListener('click', this.containerClickListener);
  }

  private async handleToggleVisibility(): Promise<void> {
    try {
      const { sdk } = this.dependencies;
      if (!sdk?.wallet) return;

      // Check if iframe is currently visible
      const isVisible = await this.isIframeVisible();

      if (!isVisible) {
        // Iframe is hidden - show it
        if (sdk.wallet.showUIOverlay) {
          await sdk.wallet.showUIOverlay();
        }
      } else {
        // Iframe is visible - navigate to home and then hide it

        if (sdk.wallet.hideUI) {
          // Navigate to home before closing
          if (sdk.wallet.showUIOverlay) {
            await sdk.wallet.showUIOverlay('/');
          }

          await sdk.wallet.hideUI();
        }
      }
    } catch (error) {
      console.warn('⚠️ [SimpleWidget] Error in handleToggleVisibility:', error);
    }
  }

  /**
   * Check if iframe is currently visible
   */
  private async isIframeVisible(): Promise<boolean> {
    try {
      const { sdk } = this.dependencies;
      if (sdk?.wallet?.isUIVisible) {
        return await sdk.wallet.isUIVisible();
      }
      // Fallback: check if view is visible
      if (sdk?.view?.isViewVisible) {
        return sdk.view.isViewVisible();
      }
      return false;
    } catch (error) {
      console.warn('⚠️ [SimpleWidget] Error checking iframe visibility:', error);
      return false;
    }
  }

  private async handleConnectWallet(): Promise<void> {
    try {
      const { sdk } = this.dependencies;
      // Ensure wallet module is available with required methods
      if (!sdk?.wallet?.isUIVisible || !sdk.wallet.hideUI || !sdk.wallet.showUIOverlay) {
        console.warn('⚠️ [SimpleWidget] Wallet module not available or missing methods');
        return;
      }

      // Check current visibility state BEFORE any actions
      const isCurrentlyVisible = await this.isIframeVisible();
      if (isCurrentlyVisible) {
        // Navigate to home before closing when using connect button
        if (sdk.wallet.showUIOverlay) {
          await sdk.wallet.showUIOverlay('/');
        }

        await sdk.wallet.hideUI();
      } else {
        // Show UI at home (no redirect to login)
        await sdk.wallet.showUIOverlay('/');
      }
    } catch (error) {
      console.warn('⚠️ [SimpleWidget] Error in handleConnectWallet:', error);
    }
  }

  /**
   * Handle recommendation click - show iframe and navigate to listing
   */
  private async handleRecommendationClick(listingId: string): Promise<void> {
    try {
      const { sdk } = this.dependencies;

      if (!sdk?.wallet?.showUIOverlay) {
        console.warn('⚠️ [SimpleWidget] SDK navigation methods not available');
        return;
      }

      // Since the iframe is already scoped to the channel (e.g., /techsan-journeys),
      // we only need the relative path within that channel
      // Construct the route path as /mission/listing-id (no channel slug needed)
      const routePath = `/mission/${listingId}`;

      // Show the iframe first if hidden
      if (sdk.view?.showView) {
        const isCurrentlyVisible = await this.isIframeVisible();
        if (!isCurrentlyVisible) {
          await sdk.view.showView();
        }
      }

      // Then navigate to the specific route
      if (sdk.view?.navigateToRoute) {
        await sdk.view.navigateToRoute(routePath);
      } else {
        // Fallback to showUIOverlay if navigateToRoute not available
        await sdk.wallet.showUIOverlay(routePath);
      }
    } catch (error) {
      console.warn('⚠️ [SimpleWidget] Error in handleRecommendationClick:', error);
    }
  }

  public destroy(): void {
    // Cleanup URL tracking and recommendations
    this.cleanupUrlTracking();

    // Cleanup event handlers
    this.cleanupEventHandlers();
    this.currentRecommendation = null;
    this.persistentRecommendations.clear();
    this.recommendationCache.clear();

    // Cleanup image loading states
    this.imageLoadingStates.clear();
    this.imageCache.clear();

    // Remove postMessage listener
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    // Remove widget from DOM
    const existingWidget = document.querySelector('.tronic-simple-widget');
    if (existingWidget) {
      existingWidget.remove();
    }
    this.container = null;
  }
}
